import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TestStepProps } from 'src/suitability/engine/types';
import { BLOCK3_QUESTIONS, type LiftAnswers } from './answers';
import {
  getBlockedObjectiveOrders,
  isObjectiveBlocked,
  RISKY_OBJECTIVE_DISABLED_REASON_KEY,
} from './gating';
import { ScoredBlock } from './scored-block';

/**
 * Bloque de objetivos de inversión, el único con reglas: con un horizonte de 3 años o
 * menos (Q21 ≤ 2), las finalidades de riesgo medio y alto (Q22 ≥ 3) se deshabilitan
 * con su motivo, y si al cambiar el horizonte la finalidad ya elegida deja de ser
 * posible, se limpia.
 *
 * Mismo patrón que el `step-block2.tsx` de Wio con P7 → P9/P10.
 */
export const StepBlock3 = ({
  answers,
  onUpdate,
}: TestStepProps<LiftAnswers>) => {
  const { t } = useTranslation();

  // Limpieza al cambiar Q21: dejar una finalidad imposible seleccionada bloquearía el
  // paso sin que se vea por qué, porque su opción ya está deshabilitada.
  useEffect(() => {
    if (isObjectiveBlocked(answers.q21, answers.q22)) {
      onUpdate({ q22: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.q21]);

  return (
    <ScoredBlock
      answers={answers}
      onUpdate={onUpdate}
      stepKey="block3"
      questions={BLOCK3_QUESTIONS}
      decorate={(question, options) => {
        if (question !== 'q22') return options;

        const blocked = getBlockedObjectiveOrders(
          answers.q21,
          options.map((option) => Number(option.value)),
        );

        return options.map((option) =>
          blocked.includes(Number(option.value))
            ? {
                ...option,
                disabled: true,
                disabledReason: t(RISKY_OBJECTIVE_DISABLED_REASON_KEY),
              }
            : option,
        );
      }}
    />
  );
};
